/**
 * BrandingContext — exposes the current tenant's org name and logo
 * URL to any authenticated page in the app (iter73.1).
 *
 * Fetches once on mount after auth is ready, then on `elara:branding-refreshed`
 * custom events (Settings fires one after save/upload so changes land
 * live without a reload).
 *
 * Fallback rules (per user spec):
 * - Logo + name     → both shown.
 * - Name only       → text name + neutral icon placeholder.
 * - Logo only       → logo + empty label (never happens in practice; backend requires a name).
 * - Nothing set     → "Elara" + Elara mark (platform default).
 *
 * Multi-tenant isolation is enforced by the backend: `/api/system/settings`
 * and `/api/system/logo` are both scoped to the authed user's tenant,
 * so one tenant's branding can never leak into another.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { systemAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const BrandingContext = createContext(null);
const DEFAULT_ORG_NAME = 'Elara';

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState({
    orgName: DEFAULT_ORG_NAME,
    logoUrl: null,
    hasCustomBrand: false, // true if tenant has set a name OR uploaded a logo
    loaded: false,
  });

  const refresh = useCallback(async () => {
    if (!user) {
      setBranding({ orgName: DEFAULT_ORG_NAME, logoUrl: null, hasCustomBrand: false, loaded: true });
      return;
    }
    try {
      // iter74 — use the role-agnostic /system/branding endpoint so
      // medics, managers and ops coordinators also see the tenant's
      // brand. The admin-only /system/settings 403'd for them and
      // silently fell back to the Elara default.
      const { data } = await systemAPI.getBranding();
      const rawName = (data?.organisation_name || '').trim();
      const nameIsCustom = rawName && rawName.toLowerCase() !== 'elara';
      const hasLogo = !!data?.organisation_logo_url;
      setBranding({
        orgName: nameIsCustom ? rawName : DEFAULT_ORG_NAME,
        logoUrl: hasLogo ? data.organisation_logo_url : null,
        hasCustomBrand: nameIsCustom || hasLogo,
        loaded: true,
      });
    } catch (err) {
      // Log so a future 403 or network error doesn't silently fall
      // back to Elara (that's what hid the bug the first time round).
      if (err?.response?.status && err.response.status !== 401) {
        console.warn('BrandingContext: failed to fetch tenant branding', err.response.status, err.response.data);
      }
      setBranding({ orgName: DEFAULT_ORG_NAME, logoUrl: null, hasCustomBrand: false, loaded: true });
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener('elara:branding-refreshed', onRefresh);
    return () => window.removeEventListener('elara:branding-refreshed', onRefresh);
  }, [refresh]);

  // Keep the browser tab title in sync with the tenant brand.
  useEffect(() => {
    if (!branding.loaded) return;
    const current = document.title.replace(/^[^·—-]+?( [—·-] )/, '');
    const prefix = branding.orgName || DEFAULT_ORG_NAME;
    // If the page hasn't set its own section (title == just the brand),
    // keep it simple; otherwise prepend the brand name.
    if (!/[—·-]/.test(document.title)) {
      document.title = `${prefix} WFM`;
    } else {
      document.title = `${prefix} — ${current.trim()}`;
    }
  }, [branding.orgName, branding.loaded]);

  const value = useMemo(() => ({ ...branding, refresh }), [branding, refresh]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) return { orgName: DEFAULT_ORG_NAME, logoUrl: null, hasCustomBrand: false, loaded: false, refresh: () => {} };
  return ctx;
}

/** Dispatchable event name so other components (Settings save handlers)
 *  can request a branding refetch without importing this file. */
export const BRANDING_REFRESH_EVENT = 'elara:branding-refreshed';
